// ── KHub chatbot launcher bridge ─────────────────────────────────────────────
// The chatbot launcher now lives in the Metronic topbar (the "help" icon beside
// the bell), but the chat panel + all its state (open/closed, unread counts)
// live in <KhubChatbotWidget>, which is mounted globally in BasePage — a
// completely separate React subtree from the vendored Metronic <Topbar>.
//
// This module is a tiny pub/sub bridge so the two can talk without coupling the
// chatbot to the global Redux store:
//   • the widget PUBLISHES a snapshot ({ available, isOpen, chatCount, broadcastCount, brandColor })
//   • the topbar button SUBSCRIBES (via useLauncherState) to render the icon + badge
//   • the widget REGISTERS a toggle handler; the button calls requestLauncherToggle()
//     on click to open/close the panel.

import { useEffect, useState } from "react";

const INITIAL_STATE = {
  available: false, // chatbot config loaded + enabled — hide the icon until then
  isOpen: false, // is the chat panel currently open
  chatCount: 0, // unread incoming live/agent chat messages
  broadcastCount: 0, // unseen broadcasts / K-News
  attention: false, // pulse the icon when something arrived while closed
  brandColor: "#e91e63",
};

let state = { ...INITIAL_STATE };
const listeners = new Set();
let toggleHandler = null;

export function getLauncherState() {
  return state;
}

// Merge a partial snapshot and notify subscribers only when something changed.
export function publishLauncherState(patch) {
  const next = { ...state, ...patch };
  if (
    next.available === state.available &&
    next.isOpen === state.isOpen &&
    next.chatCount === state.chatCount &&
    next.broadcastCount === state.broadcastCount &&
    next.attention === state.attention &&
    next.brandColor === state.brandColor
  ) {
    return; // no-op — avoids redundant re-renders in the topbar
  }
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function subscribeLauncher(fn) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// The widget tells the bridge how to handle an open/close request.
export function registerLauncherToggle(fn) {
  toggleHandler = fn;
  return () => {
    if (toggleHandler === fn) toggleHandler = null;
  };
}

// The topbar button asks the widget to toggle. No-op if no widget is mounted.
export function requestLauncherToggle() {
  if (toggleHandler) toggleHandler();
}

// Test-only: restore module singletons to a clean slate between tests.
export function __resetLauncherBridge() {
  state = { ...INITIAL_STATE };
  listeners.clear();
  toggleHandler = null;
}

// React hook consumed by the topbar launcher button.
export function useLauncherState() {
  const [snapshot, setSnapshot] = useState(getLauncherState);
  useEffect(() => {
    // Sync any change that landed between the initial render and subscribe.
    setSnapshot(getLauncherState());
    return subscribeLauncher(setSnapshot);
  }, []);
  return snapshot;
}
